import { createMockMailCryptoClient } from '@platform/api-client/testing';
/**
 * Mock implementation of `WebmailApi`: a deterministic mailbox (fictional people) with a large
 * inbox to exercise the virtualized list, plus a few messages that test the sanitizer
 * (remote images, scripts, tracking pixels).
 */
import { plainTextToMailHtml, type MessageAnalysisView, type RichTextValue } from '@platform/ui';
import { createMockMfaClient } from '@platform/api-client/testing';
import type { WebmailApi } from './index';
import { PAGE_SIZE } from './constants';
import { createMockPeople } from './mock-people';
import { createMockGroupware } from './mock-groupware';
import { BOB, createMockCalendar, INVITE_MESSAGE_ID } from './mock-calendar';
import { createMockSignatures } from './mock-signatures';
import { createMockAutomaticReplies } from './mock-automatic-replies';
import { createMockScopedLists } from './mock-scoped-lists';
import { createMockCalendarExtra } from './calendar-extra';
import { createMockQuarantineMe } from './quarantine';
import { createMockCalendarFeeds } from './calendar-feeds';
// Analyses + raw sources produced by the real analyzer (services/platform-api message-analysis) on its
// test fixtures (test/fixtures/headers.ts), so the details dialog shows realistic data.
import mockMessages from './mock-messages.json';
import type { Address, AttachedMessage, Folder, Message, MessageAttachment, MessageSummary, Session, UploadedFile } from './types';
import { ApiError, type Draft, type DraftInput, type MyPublicFolder } from '@platform/api-client';
import { matchesSearchQuery, parseSearchQuery, type SearchDocument } from '@platform/search-query';

/** Stored message: the API model plus the folder it lives in. */
type Stored = Omit<Message, 'replyTo' | 'attachments'> & { folderId: string; attachments?: Message['attachments'] };

type MockSample = keyof typeof mockMessages.analyses;
const ANALYSES = mockMessages.analyses as unknown as Record<MockSample, MessageAnalysisView>;

/** Which sample analysis/source a mock message shows. */
function sampleOf(message: Stored): MockSample {
  if (message.id === 'm-phish') return 'spoofed';
  if (message.id === 'm-fwd') return 'multiHop';
  return message.from.address.endsWith('@example.com') ? 'internal' : 'gmail';
}

const ATTACHED_MESSAGE: AttachedMessage = {
  subject: 'Quarterly planning',
  from: { name: 'Clara Conrad', address: 'clara.conrad@example.org' },
  to: [{ name: 'Felix Fuchs', address: 'felix.fuchs@example.com' }],
  cc: [],
  sentAt: '2026-10-08T14:12:00.000Z',
  html: '<p>Hello,</p><p>Can we move the planning session to Thursday afternoon? I have a conflict on Wednesday.</p><p>Best regards<br>Clara</p>',
  attachments: [{ name: 'agenda.pdf', size: 48213 }],
};

const SESSION_KEY = 'platform.webmail.mock-session';

/** Inline picture of the mock message `m-inline` (a small bar chart as SVG data: URL, content colors in the JSON). */
const MOCK_CHART_CID = 'chart@mock.example';
const MOCK_CHART_URL = mockMessages.chartPicture;

const MOCK_LIMITS = { maxFileBytes: 25 * 1024 * 1024, maxTotalBytes: 25 * 1024 * 1024, maxCount: 20 };

const LATENCY_MS = 200;
const INBOX_SIZE = 2000;

function delay<T>(value: T, ms = LATENCY_MS): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(structuredClone(value)), ms));
}

const PEOPLE: Address[] = [
  { name: 'Clara Conrad', address: 'clara.conrad@example.org' },
  { name: 'David Dietz', address: 'david.dietz@example.com' },
  { name: 'Eva Engel', address: 'eva.engel@example.org' },
  { name: 'Felix Fuchs', address: 'felix.fuchs@example.com' },
  { name: 'Greta Graf', address: 'greta.graf@example.net' },
  { name: 'Service desk', address: 'servicedesk@example.com' },
  { name: 'Newsletter', address: 'news@shop.example' },
];

const TOPICS = [
  ['Quarterly planning', 'Can we move the planning session to Thursday afternoon? I have a conflict on Wednesday.'],
  ['Invoice 2026-10', 'Please find the invoice for October attached. Payment terms are 30 days as usual.'],
  ['Server maintenance tonight', 'The mail server will be restarted at 22:00 for updates. Expect a short interruption.'],
  ['Lunch?', 'Anyone up for lunch at the new place around the corner? I can book a table for four.'],
  ['Re: Contract draft', 'Thanks for the comments. I incorporated them, see the updated draft in the shared folder.'],
  ['Welcome to the team', 'We are happy to have you on board. Your laptop will be ready on Monday morning.'],
  ['Travel request approved', 'Your travel request for the trade fair has been approved. Have a good trip!'],
] as const;

function paragraphs(text: string): string {
  return `<p>Hello,</p><p>${text}</p><p>Best regards</p>`;
}

function buildFolders(): Folder[] {
  return [
    { id: 'inbox', wellKnown: 'inbox', name: 'Inbox', depth: 0, unread: 0, total: 0 },
    { id: 'projects', name: 'Projects', depth: 1, unread: 0, total: 0 },
    { id: 'shared-root:dGVhbUBleGFtcGxlLmNvbQ', mailboxRoot: true, name: 'Team Test', depth: 0, unread: 0, total: 0 },
    { id: 'shared-team-inbox', mailboxContext: true, wellKnown: 'inbox', name: 'Inbox', depth: 1, unread: 0, total: 0 },
    { id: 'shared-team-projects', mailboxContext: true, name: 'Projects', depth: 2, unread: 0, total: 0 },
    { id: 'drafts', wellKnown: 'drafts', name: 'Drafts', depth: 0, unread: 0, total: 0 },
    { id: 'sent', wellKnown: 'sent', name: 'Sent Items', depth: 0, unread: 0, total: 0 },
    { id: 'archive', wellKnown: 'archive', name: 'Archive', depth: 0, unread: 0, total: 0 },
    { id: 'junk', wellKnown: 'junk', name: 'Junk Email', depth: 0, unread: 0, total: 0 },
    { id: 'deleted', wellKnown: 'deleted', name: 'Deleted Items', depth: 0, unread: 0, total: 0 },
  ];
}

function buildMessages(me: Address): Stored[] {
  const now = Date.UTC(2026, 9, 9, 7, 30);
  const featured: Stored[] = [
    {
      id: INVITE_MESSAGE_ID,
      folderId: 'inbox',
      from: BOB,
      to: [me],
      cc: [],
      subject: 'Invitation: Budget review',
      preview: 'Bob invites you to a meeting on Friday in Room 1.',
      receivedAt: new Date(now - 60_000).toISOString(),
      unread: true,
      flagged: false,
      hasAttachments: false,
      html: '<p>Let us go through the Q4 budget.</p>',
    },
    {
      id: 'm-phish',
      folderId: 'inbox',
      from: { name: 'Alice Example', address: 'alice@examp1e.com' },
      to: [me],
      cc: [],
      subject: 'Dringend: Ueberweisung heute',
      preview: 'Phishing sample: name of a colleague, lookalike domain, Reply-To at a freemail address. Open Message details.',
      receivedAt: new Date(now - 2 * 60_000).toISOString(),
      unread: true,
      flagged: false,
      hasAttachments: false,
      html: '<p>Hi,</p><p>please transfer 9,800 EUR to the new supplier account today. I am in a meeting, reply by mail only.</p><p>Alice</p>',
    },
    {
      id: 'm-fwd',
      folderId: 'inbox',
      from: PEOPLE[3]!,
      to: [me],
      cc: [],
      subject: 'Fwd: Quarterly planning',
      preview: 'Forwarded as attachment: open the attached message below the header.',
      receivedAt: new Date(now - 3 * 60_000).toISOString(),
      unread: true,
      flagged: false,
      hasAttachments: true,
      html: '<p>See the attached mail from Clara.</p><p>Felix</p>',
      attachments: [
        { id: 'a-fwd-1', name: 'Quarterly planning', kind: 'item', size: 2481 },
        { id: 'a-fwd-2', name: 'notes.txt', kind: 'file', size: 812 },
      ],
    },
    {
      id: 'm-remote',
      folderId: 'inbox',
      from: PEOPLE[6]!,
      to: [me],
      cc: [],
      subject: 'Autumn sale: 20 % off everything',
      preview: 'This message loads remote images and contains a script that must never run.',
      receivedAt: new Date(now - 5 * 60_000).toISOString(),
      unread: true,
      flagged: false,
      hasAttachments: false,
      html: [
        '<h1 style="font-family: Georgia, serif; color: darkslateblue">Autumn sale</h1>',
        '<p>Remote banner below (blocked by default):</p>',
        '<img src="https://images.shop.example/banner.jpg" alt="Sale banner" width="480" height="160">',
        '<img src="https://track.shop.example/open.gif?u=42" width="1" height="1" alt="">',
        '<div style="background-image: url(https://images.shop.example/bg.png); padding: 12px">Background from a remote server.</div>',
        '<script>document.body.innerHTML = "hacked"</script>',
        '<p><a href="https://shop.example/sale" onclick="alert(1)">Shop now</a></p>',
      ].join(''),
    },
    {
      id: 'm-newsletter',
      folderId: 'inbox',
      from: PEOPLE[4]!,
      to: [me],
      cc: [],
      subject: 'Platform newsletter: October',
      preview: 'A full HTML document with <style> blocks in the head (Outlook comment wrapper), like real newsletters.',
      receivedAt: new Date(now - 20 * 60_000).toISOString(),
      unread: false,
      flagged: false,
      hasAttachments: false,
      html: [
        '<!doctype html><html><head><style type="text/css"><!--',
        '.wrap { max-width: 560px; margin: 0 auto; font-family: Georgia, serif }',
        '.hero { background: midnightblue; color: white; padding: 24px; border-radius: 8px }',
        '.hero h1 { margin: 0; font-size: 26px; letter-spacing: 1px }',
        '.cols td { width: 50%; vertical-align: top; padding: 12px; background: whitesmoke; color: black }',
        '.btn { display: inline-block; padding: 8px 16px; background: darkorange; color: white; text-decoration: none; border-radius: 4px }',
        '.banner { position: fixed; top: 0; left: 0; right: 0 }',
        '--></style></head><body>',
        '<div class="wrap"><div class="hero"><h1>October update</h1><p>Styles come from the head of this mail.</p></div>',
        '<table class="cols" width="100%"><tr><td><b>Webmail</b><br>Phones get a card list and a new-message button.</td>',
        '<td><b>Admin</b><br>Numbers are right-aligned in tables.</td></tr></table>',
        '<p><a class="btn" href="https://example.org/news">Read more</a></p></div>',
        '</body></html>',
      ].join(''),
    },
    {
      id: 'm-welcome',
      folderId: 'inbox',
      from: PEOPLE[5]!,
      to: [me],
      cc: [PEOPLE[1]!],
      subject: 'Your new mailbox is ready',
      preview: 'Welcome to the new mail platform. Here is how to set up Outlook and your phone.',
      receivedAt: new Date(now - 60 * 60_000).toISOString(),
      unread: true,
      flagged: true,
      hasAttachments: true,
      html: [
        '<p>Welcome to the new mail platform!</p>',
        '<ul><li><b>Outlook</b>: add your account, autodiscover does the rest.</li>',
        '<li><b>Phone</b>: use Exchange ActiveSync with your usual password.</li>',
        '<li><b>Thunderbird</b>: IMAP is detected automatically.</li></ul>',
        '<table border="1" cellpadding="6"><tr><th>Service</th><th>Address</th></tr>',
        '<tr><td>Webmail</td><td>https://mail.example.com/webmail</td></tr></table>',
      ].join(''),
    },
    {
      id: 'm-inline',
      folderId: 'inbox',
      from: { name: 'Carol Example', address: 'carol@example.com' },
      to: [me],
      cc: [{ name: 'Dave Example', address: 'dave@example.com' }],
      subject: 'Q4 figures with chart',
      preview: 'An HTML mail with an inline chart (cid:) and a PDF: try Reply and Forward.',
      receivedAt: new Date(now - 4 * 60_000).toISOString(),
      unread: true,
      flagged: false,
      hasAttachments: true,
      html: `<p>Hi,</p><p>here are the <b>Q4 figures</b>. Revenue is up 12 %.</p><p><img src="cid:${MOCK_CHART_CID}" alt="chart.png" width="240" height="96"></p><p>Best regards<br>Carol</p>`,
      attachments: [{ id: 'a-inline-1', name: 'Q4-report.pdf', kind: 'file', size: 482_113 }],
    },
    {
      id: 'm-plain',
      text: 'Hi Alice,\n\nthe nightly backup finished without errors.\n\n  Duration: 42 min\n  Size:     118 GB\n\n> Quoted line from the previous report.\n\n-- \nFelix',
      folderId: 'inbox',
      from: PEOPLE[3]!,
      to: [me],
      cc: [],
      subject: 'Plain text: backup report',
      preview: 'A text/plain mail, converted to HTML like gromox does (one <pre>).',
      receivedAt: new Date(now - 90 * 60_000).toISOString(),
      unread: false,
      flagged: false,
      hasAttachments: false,
      html: plainTextToMailHtml(
        'Hi Alice,\n\nthe nightly backup finished without errors.\n\n  Duration: 42 min\n  Size:     118 GB\n\n> Quoted line from the previous report.\n\n-- \nFelix',
      ),
    },
  ];

  const generated = Array.from({ length: INBOX_SIZE }, (_, index): Stored => {
    const [subject, text] = TOPICS[index % TOPICS.length]!;
    const from = PEOPLE[index % (PEOPLE.length - 1)]!;
    return {
      id: `m-${index}`,
      folderId: index % 97 === 13 ? 'projects' : 'inbox',
      from,
      to: [me],
      cc: index % 5 === 0 ? [PEOPLE[(index + 2) % PEOPLE.length]!] : [],
      subject: index % 7 === 3 ? `${subject} (${index})` : subject,
      preview: text,
      receivedAt: new Date(now - (index + 2) * 47 * 60_000).toISOString(),
      unread: index < 12 || index % 23 === 0,
      flagged: index % 31 === 0,
      hasAttachments: index % 7 === 1,
      html: paragraphs(text),
    };
  });

  const sent: Stored[] = TOPICS.slice(0, 4).map(([subject, text], index) => ({
    id: `s-${index}`,
    folderId: 'sent',
    from: me,
    to: [PEOPLE[index]!],
    cc: [],
    subject: `Re: ${subject}`,
    preview: 'Thanks, sounds good to me.',
    receivedAt: new Date(now - (index + 1) * 3 * 3_600_000).toISOString(),
    unread: false,
    flagged: false,
    hasAttachments: false,
    html: paragraphs(`Thanks, sounds good to me.</p><blockquote>${text}</blockquote><p>`),
  }));

  return [...featured, ...generated, ...sent];
}


function readSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function writeSession(session: Session | null): void {
  try {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Without storage the mock session lasts until reload.
  }
}

function addressOf(username: string): Address {
  const local = username.split('@')[0] ?? username;
  return { name: local.charAt(0).toUpperCase() + local.slice(1), address: username.includes('@') ? username : `${local}@example.com` };
}

export function createMockApi(): WebmailApi {
  const publicFolders: MyPublicFolder[] = [
    { id: 'public-news', parentId: null, name: 'Announcements', kind: 'mail', childFolderCount: 1, readable: true, totalCount: 1, unreadCount: 1 },
    { id: 'public-visible', parentId: null, name: 'Team reference', kind: 'mail', childFolderCount: 0, readable: false, totalCount: null, unreadCount: null },
    { id: 'public-archive', parentId: 'public-news', name: 'Previous announcements', kind: 'mail', childFolderCount: 0, readable: true, totalCount: 0, unreadCount: 0 },
  ];
  const restored = readSession();
  let me: Address = restored ? addressOf(restored.username) : { name: 'Alice Example', address: 'alice@example.com' };
  let messages = buildMessages(me);
  const folders = buildFolders();

  const summarize = ({ to: _to, cc: _cc, html: _html, folderId: _folder, ...summary }: Stored): MessageSummary => summary;
  const detail = ({ folderId: _folder, attachments = [], ...message }: Stored): Message => ({
    ...message,
    attachments,
    replyTo: message.id === 'm-phish' ? [{ name: 'Alice Example', address: 'alice.example.ceo@gmail.com' }] : [],
  });
  const find = (id: string) => {
    const message = messages.find((candidate) => candidate.id === id);
    if (!message) throw new Error(`Unknown message ${id}`);
    return message;
  };
  const store = (folderId: string, to: readonly Address[], cc: readonly Address[], subject: string, body: RichTextValue) => {
    const html = body.format === 'html' ? body.content : plainTextToMailHtml(body.content);
    messages.push({
      id: `s-${Date.now()}-${messages.length}`,
      folderId,
      from: me,
      to,
      cc,
      subject,
      preview: (new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '').slice(0, 120),
      receivedAt: new Date().toISOString(),
      unread: false,
      flagged: false,
      hasAttachments: false,
      html,
    });
  };
  const toAddresses = (list: readonly Address[]) => list.map(({ name, address }) => ({ name: name || address, address }));
  const uploadsById = new Map<string, UploadedFile>();
  const takeUploads = (ids: readonly string[]) =>
    ids.flatMap((id) => {
      const file = uploadsById.get(id);
      uploadsById.delete(id);
      return file ? [{ name: file.name, kind: 'file' as const, size: file.size }] : [];
    });
  /** Attachments of the message `store` just added. */
  const attach = (list: readonly Omit<MessageAttachment, 'id'>[]) => {
    const sent = messages.at(-1);
    if (!sent || list.length === 0) return;
    sent.hasAttachments = true;
    sent.attachments = list.map((attachment, index) => ({ ...attachment, id: `${sent.id}-a${index}` }));
  };
  const people = createMockPeople(() => me);
  const { meetingOf, ...calendarApi } = createMockCalendar(() => me);
  const calendar = { meetingOf };
  const signatures = createMockSignatures(() => me);
  const drafts = new Map<string, Draft>();
  const saveDraft = async (input: DraftInput, previousId?: string) => {
    await delay(null, 0);
    const previous = previousId ? drafts.get(previousId) : undefined;
    if (previousId && !previous) throw new ApiError(404, 'draft_not_found', 'Draft not found');
    if (previous && input.version && input.version !== previous.version) throw new ApiError(409, 'draft_changed', 'Draft changed');
    const to = (input.to ?? []).map(a => ({ name: a.name ?? '', address: a.email }));
    const cc = (input.cc ?? []).map(a => ({ name: a.name ?? '', address: a.email }));
    store('drafts', to, cc, input.subject ?? '', { format: input.body.type, content: input.body.content });
    const message = messages.at(-1)!;
    const kept = (previous?.attachments ?? []).filter(a => input.draftAttachmentIds?.includes(a.id));
    const uploadIds = input.uploadIds ?? [];
    const sourceRefs = [...kept.map(a => ({ source: 'draft' as const, ref: a.id })), ...uploadIds.map(ref => ({ source: 'upload' as const, ref }))];
    attach([...kept, ...takeUploads(uploadIds)]);
    const draft: Draft = { id: message.id, version: crypto.randomUUID(), savedAt: new Date().toISOString(), attachments: (message.attachments ?? []).map((a, index) => ({ ...a, contentType: null, size: a.size ?? null, kind: a.kind, sourceRef: sourceRefs[index] })), from: { name: me.name, email: input.from ?? me.address }, to: (input.to ?? []).map(a => ({ name: a.name ?? null, email: a.email })), cc: (input.cc ?? []).map(a => ({ name: a.name ?? null, email: a.email })), bcc: (input.bcc ?? []).map(a => ({ name: a.name ?? null, email: a.email })), subject: input.subject ?? '', body: input.body, inReplyTo: null, references: null };
    drafts.set(draft.id, draft);
    // Save as new: keep the source draft; only the user can explicitly discard a saved item.
    return { id: draft.id, version: draft.version, savedAt: draft.savedAt, attachments: draft.attachments };
  };
  /** The folder and its subfolders (tree order: the following deeper entries). */
  const subtree = (id: string): Folder[] => {
    const start = folders.findIndex((folder) => folder.id === id);
    const root = folders[start];
    if (!root) return [];
    const end = folders.findIndex((folder, index) => index > start && folder.depth <= root.depth);
    return folders.slice(start, end < 0 ? undefined : end);
  };

  return {
    mailCrypto: createMockMailCryptoClient(),
    mfa: createMockMfaClient(),
    groupware: createMockGroupware(),
    async login(username, password) {
      if (!username.trim() || !password || password === 'wrong') {
        await delay(null);
        throw new Error('Invalid credentials');
      }
      me = addressOf(username);
      messages = buildMessages(me);
      const session: Session = { username: me.address, displayName: me.name };
      writeSession(session);
      return delay(session);
    },
    async logout() {
      writeSession(null);
      await delay(null, 50);
    },
    restoreSession: () => delay(readSession(), 0),
    listPublicFolders: (query = {}) => {
      const visible = publicFolders.filter(folder => folder.parentId === (query.parent ?? null));
      const offset = query.offset ?? 0, limit = query.limit ?? PAGE_SIZE;
      return delay({ folders: visible.slice(offset, offset + limit), total: visible.length, offset, nextOffset: offset + limit < visible.length ? offset + limit : null });
    },
    listPublicMessages: (folderId, offset = 0) => {
      if (!publicFolders.some(folder => folder.id === folderId && folder.readable)) return Promise.reject(new ApiError(404, 'not_found', 'No such public folder'));
      const sample = { ...summarize(find('m-welcome')), id: 'public-message', hasAttachments: false };
      return delay({ items: folderId === 'public-news' && offset === 0 ? [sample] : [], total: folderId === 'public-news' ? 1 : 0, nextOffset: null });
    },
    getPublicMessage: (folderId, itemId) => {
      if (folderId !== 'public-news' || itemId !== 'public-message') return Promise.reject(new ApiError(404, 'not_found', 'No such public message'));
      return delay({ ...detail(find('m-welcome')), id: 'public-message', hasAttachments: false, attachments: [], meeting: null });
    },
    listFolders: () =>
      delay(
        folders.map((folder) => {
          const inFolder = messages.filter((message) => message.folderId === folder.id);
          return { ...folder, total: inFolder.length, unread: inFolder.filter((message) => message.unread).length };
        }),
      ),
    listMessages(folderId, offset = 0) {
      const inFolder = messages.filter((message) => message.folderId === folderId).sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
      const items = inFolder.slice(offset, offset + PAGE_SIZE).map(summarize);
      const end = offset + items.length;
      return delay({ items, total: inFolder.length, nextOffset: end < inFolder.length ? end : null });
    },
    searchMessages(input) {
      const parsed = parseSearchQuery(input.query ?? '', input.locale);
      if (!parsed.ok) throw new ApiError(422, parsed.error.code, 'Search query is not supported');
      let targetFolders = folders.map((folder) => folder.id);
      if (input.scope.kind === 'folder') targetFolders = [input.scope.folderId];
      if (input.scope.kind === 'folderTree') {
        const rootFolderId = input.scope.folderId;
        const start = folders.findIndex((folder) => folder.id === rootFolderId);
        if (start >= 0) {
          const depth = folders[start]?.depth ?? 0;
          const subtree: string[] = [];
          for (const folder of folders.slice(start)) {
            if (subtree.length > 0 && folder.depth <= depth) break;
            subtree.push(folder.id);
          }
          targetFolders = subtree;
        }
      }
      const found = messages.filter((message) => targetFolders.includes(message.folderId)).filter((message) => {
        const doc: SearchDocument = {
          subject: message.subject,
          body: message.text ?? message.preview,
          from: { name: message.from.name, email: message.from.address },
          to: message.to.map((address) => ({ name: address.name, email: address.address })),
          cc: message.cc.map((address) => ({ name: address.name, email: address.address })),
          attachmentNames: (message.attachments ?? []).map((attachment) => attachment.name),
          receivedAt: message.receivedAt,
          sentAt: message.sentAt,
          isRead: !message.unread,
          flagged: message.flagged,
          hasAttachments: message.hasAttachments,
        };
        return matchesSearchQuery(parsed.ast, doc);
      }).sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
      const offset = Number(input.cursor?.replace(/^mock:/, '') ?? 0);
      const pageSize = input.pageSize ?? 25;
      const page = found.slice(offset, offset + pageSize);
      const end = offset + page.length;
      return delay({
        items: page.map((message) => ({ ...summarize(message), folderId: message.folderId, folderName: folders.find((folder) => folder.id === message.folderId)?.name ?? message.folderId })),
        cursor: end < found.length ? `mock:${end}` : null,
        engine: 'index+tail' as const,
        unindexable: 3,
        complete: end >= found.length,
        scanned: { folders: targetFolders.length, items: end },
        skipped: [],
      });
    },
    getMessage: (id) => delay({ ...detail(find(id)), ...(calendar.meetingOf(id) && { meeting: calendar.meetingOf(id) }) }, 120),
    saveDraft: input => saveDraft(input),
    replaceDraft: (id, input) => saveDraft(input, id),
    getDraft: async id => {
      await delay(null, 0);
      const draft = drafts.get(id);
      if (!draft) throw new ApiError(404, 'draft_not_found', 'Draft not found');
      return draft;
    },
    sendDraft: async (id, input) => {
      await delay(null, 0);
      const draft = drafts.get(id);
      if (!draft) throw new ApiError(404, 'draft_not_found', 'Draft not found');
      store('sent', (input.to ?? []).map(a => ({ name: a.name ?? '', address: a.email })), [], input.subject ?? '', { format: input.body.type, content: input.body.content });
      attach([...draft.attachments, ...takeUploads(input.uploadIds ?? [])]);
      return { draftDeleted: false };
    },
    async setRead(id, read) {
      find(id).unread = !read;
      await delay(null, 50);
    },
    async deleteMessage(id) {
      const message = find(id);
      if (message.folderId === 'deleted') messages = messages.filter((candidate) => candidate.id !== id);
      else message.folderId = 'deleted';
      await delay(null, 100);
    },
    async send({ to, cc, bcc, subject, body, attachItemIds = [], uploadIds = [] }) {
      people.remember([...to, ...cc, ...bcc]);
      store('sent', toAddresses(to), toAddresses(cc), subject, body);
      attach([...attachItemIds.map((id) => ({ name: find(id).subject, kind: 'item' as const, size: 2048 })), ...takeUploads(uploadIds)]);
      await delay(null, 400);
    },
    async reply(id, { body, replyAll, uploadIds = [] }) {
      const original = find(id);
      const cc = replyAll ? [...original.to, ...original.cc].filter((address) => address.address !== me.address) : [];
      store('sent', [original.from], cc, `Re: ${original.subject}`, body);
      attach(takeUploads(uploadIds));
      await delay(null, 400);
    },
    async forward(id, { to, cc, bcc, body, originalAttachmentIds, uploadIds = [] }) {
      people.remember([...to, ...cc, ...bcc]);
      const original = find(id);
      store('sent', toAddresses(to), toAddresses(cc), `Fwd: ${original.subject}`, body);
      const kept = (original.attachments ?? []).filter((a) => originalAttachmentIds.includes(a.id));
      attach([...kept, ...takeUploads(uploadIds)]);
      await delay(null, 400);
    },
    attachmentLimits: () => delay(MOCK_LIMITS, 0),
    uploadAttachment(file, { onProgress, signal } = {}) {
      // Simulated upload: ten progress steps over about a second.
      return new Promise((resolve, reject) => {
        let step = 0;
        const timer = setInterval(() => {
          step += 1;
          onProgress?.(step / 10);
          if (step < 10) return;
          clearInterval(timer);
          const uploaded = { id: crypto.randomUUID(), name: file.name, contentType: file.type || 'application/octet-stream', size: file.size };
          uploadsById.set(uploaded.id, uploaded);
          resolve(uploaded);
        }, 100);
        signal?.addEventListener('abort', () => {
          clearInterval(timer);
          reject(new DOMException('The upload was cancelled.', 'AbortError'));
        });
      });
    },
    async deleteUpload(id) {
      uploadsById.delete(id);
      await delay(null, 50);
    },
    inlineImageUrl: (_messageId, contentId) => (contentId === MOCK_CHART_CID ? MOCK_CHART_URL : undefined),
    attachmentDownloadUrl: (_messageId, attachmentId) => `data:application/octet-stream;charset=utf-8,${encodeURIComponent(`mock attachment ${attachmentId}`)}`,
    async setFlag(ids, flagged) {
      for (const id of ids) find(id).flagged = flagged;
      await delay(null, 50);
    },
    async moveMessages(ids, folderId) {
      for (const id of ids) find(id).folderId = folderId;
      await delay(null, 100);
    },
    async copyMessages(ids, folderId) {
      for (const id of ids) messages.push({ ...structuredClone(find(id)), id: `${id}-copy-${Date.now()}`, folderId });
      await delay(null, 100);
    },
    async createFolder(parentId, name) {
      const index = folders.findIndex((folder) => folder.id === parentId);
      const parent = folders[index];
      if (!parent) throw new Error(`Unknown folder ${parentId}`);
      folders.splice(index + subtree(parentId).length, 0, { id: `f-${Date.now()}`, name, depth: parent.depth + 1, unread: 0, total: 0 });
      await delay(null, 100);
    },
    async renameFolder(id, name) {
      const folder = folders.find((candidate) => candidate.id === id);
      if (folder) folder.name = name;
      await delay(null, 100);
    },
    async deleteFolder(id) {
      const removed = subtree(id);
      const ids = new Set(removed.map((folder) => folder.id));
      for (const message of messages) if (ids.has(message.folderId)) message.folderId = 'deleted';
      folders.splice(folders.indexOf(removed[0]!), removed.length);
      await delay(null, 100);
    },
    async markAllRead(folderId) {
      for (const message of messages) if (message.folderId === folderId) message.unread = false;
      await delay(null, 100);
    },
    async emptyFolder(folderId) {
      messages = messages.filter((message) => message.folderId !== folderId);
      await delay(null, 100);
    },
    async importMessage(_folderId, _file, { onProgress } = {}) {
      onProgress?.(1);
      await delay(null, 200);
    },
    // No platform API in preview mode: built-in branding and languages.
    runtime: undefined,
    ...people.api,
    ...calendarApi,
    ...createMockCalendarExtra(calendarApi),
    ...createMockQuarantineMe(),
    ...createMockCalendarFeeds(),
    ...signatures.api,
    myInboxRules: () => Promise.resolve({ state: 'unavailable' as const, reason: 'native_response_incomplete' as const, outlookRuleBlobExists: null, readOnly: true as const, rules: [] }),
    saveMyInboxRules:()=>Promise.reject(new Error('Native rules editing is unavailable in preview mode')),
    ...createMockAutomaticReplies().api,
    ...createMockScopedLists().api,
    getSource(id) {
      const text = mockMessages.sources[sampleOf(find(id))];
      return delay({ text, origin: 'imap' as const, size: new TextEncoder().encode(text).length, truncated: false });
    },
    sourceDownloadUrl: (id) => `data:message/rfc822;charset=utf-8,${encodeURIComponent(mockMessages.sources[sampleOf(find(id))])}`,
    getAnalysis: (id) => delay(ANALYSES[sampleOf(find(id))], 300),
    getAttachedMessage: () => delay(ATTACHED_MESSAGE),
  };
}
